import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import { BackListButton, Loading } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import ProductType from '@/metrics/components/ProductType/ProductType'
import { groupByValues } from '@/metrics/constants'
import { getTitleByOrdering } from '@/metrics/helpers/helpers'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import CollectionMetrics from './CollectionMetrics/CollectionMetrics'
import * as Styles from './styles'

const CollectionMetricDetailPage = ({ isUnifiedDashboard }) => {
  const { collectionPosition } = useParams()
  const { info } = useContext(AppContext)
  const { t } = useTranslation()
  const { regions } = useRegions()

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated

  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  } = useFilters()

  const { data: collections, loading: loadingCollection } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReturnedProducts,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: {
      groupBy: groupByValues.collections,
      ordering: productsFilters?.ordering,
      query: productsFilters?.query,
    },
    doRequest: !!collectionPosition,
  })

  const collection = collections?.results?.[collectionPosition]

  const getBackUrl = () => {
    return `${isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage}?${getUrlSearchParams()}`
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getBackUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={TShirtIcon}
        title={getTitleByOrdering('Colecciones', productsFilters?.ordering, t)}
        loading={loadingCollection}
        item={
          collection && (
            <ProductType productType={{ ...collection, index: Number(collectionPosition) + 1 }} showAmounts={false} />
          )
        }
        count={collection?.count}
        percentage={collection?.percentage}
        sales={collection?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated}
      />
      {collection && (
        <Loading loading={loadingCollection}>
          <CollectionMetrics
            collectionId={collection.id}
            returnPortalId={isUnifiedDashboard ? null : info?.selectedReturnPortal?.id}
            commonFilters={commonFilters}
            returnSalesRatioFilters={returnSalesRatioFilters}
            setReturnSalesRatioFilters={setReturnSalesRatioFilters}
            returnsAndExchangesRatioFilters={returnsAndExchangesRatioFilters}
            setReturnsAndExchangesRatioFilters={setReturnsAndExchangesRatioFilters}
            collectionPosition={collectionPosition}
            salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
            productsOrdering={productsFilters?.ordering}
            isUnifiedDashboard={isUnifiedDashboard}
          />
        </Loading>
      )}
    </Styles.Container>
  )
}

export default CollectionMetricDetailPage
